import {NgTemplateOutlet} from '@angular/common';
import {Component, HostListener, ViewEncapsulation, inject, DOCUMENT} from '@angular/core';
import {ChildrenOutletContexts} from '@angular/router';
import {DebugCard, DebugNode} from '../model/debug-card';
import {discoverComponentTree, discoverRoutedComponent} from '../util/page-discovery';
import {
  collectPageObjects,
  filterCards,
  setAllExpanded,
  setExpansionLevel,
} from '../util/safe-format';

@Component({
  templateUrl: './debug.dlg-component.html',
  styleUrl: './debug.dlg-component.css',
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet],
})
export class DebugDlgComponent {
  closeDialog: (() => void) | undefined;

  private readonly document = inject(DOCUMENT);
  private readonly outletContexts = inject(ChildrenOutletContexts, {optional: true});

  private treeCard: DebugCard | null = null;
  cards: DebugCard[] = [];
  displayCards: DebugCard[] = [];
  query = '';
  error?: string;
  inspectedName = '';

  constructor() {
    try {
      this.treeCard = discoverComponentTree(this.document, this.outletContexts);
      const instance = discoverRoutedComponent(this.document, this.outletContexts);
      this.cards = this.buildAllCards(instance);
      this.displayCards = this.cards;
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  inspectNode(node: DebugNode): void {
    if (!node.instance) return;
    this.inspectedName = node.field;
    this.cards = this.buildAllCards(node.instance);
    this.displayCards = this.query ? filterCards(this.cards, this.query) : this.cards;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDialog?.();
  }

  onSearch(value: string): void {
    this.query = value;
    this.displayCards = filterCards(this.cards, value);
  }

  collapseAll(): void {
    this.cards.forEach((card) => {
      card.expanded.set(false);
      setAllExpanded(card.rows, false);
    });
  }

  expandAll(): void {
    this.cards.forEach((card) => {
      card.expanded.set(true);
      setAllExpanded(card.rows, true);
    });
  }

  expandToLevel(level: number): void {
    this.cards.forEach((card) => {
      card.expanded.set(true);
      setExpansionLevel(card.rows, level);
    });
  }

  collapseCardRows(card: DebugCard): void {
    card.expanded.set(true);
    setAllExpanded(card.rows, false);
  }

  expandCardToLevel(card: DebugCard, level: number): void {
    card.expanded.set(true);
    setExpansionLevel(card.rows, level);
  }

  expandCardAll(card: DebugCard): void {
    card.expanded.set(true);
    setAllExpanded(card.rows, true);
  }

  private buildAllCards(instance: unknown): DebugCard[] {
    const cards: DebugCard[] = [];
    if (this.treeCard) cards.push(this.treeCard);
    cards.push(...collectPageObjects(instance));
    return cards;
  }
}
